import { useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, Lock, ArrowRight } from 'lucide-react';
import { AuthLayout, AuthInput, AuthButton } from './AuthComponents';
// import { StravaButton } from './StravaButton';
import { GoogleButton } from './GoogleButton';
import { useAuth } from '@/context/AuthContext';
import { LOGIN_MUTATION } from '@/graphql/mutations';
import { ErrorBanner } from '@/components/ui/ErrorBanner';

export function Login() {
    const navigate = useNavigate();
    const location = useLocation();
    const { login } = useAuth();
    const { t } = useTranslation('auth');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState(
        (location.state as { stravaError?: string; googleError?: string })?.stravaError
        ?? (location.state as { googleError?: string })?.googleError
        ?? ''
    );

    const [loginMutation, { loading }] = useMutation(LOGIN_MUTATION);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!email || !password) {
            setError(t('login.error.fillAll'));
            return;
        }

        try {
            const { data } = await loginMutation({ variables: { input: { email, password } } });

            if (data?.login?.token) {
                await login(data.login.token);
                navigate('/');
            }
        } catch (err: unknown) {
            console.error(err);
            setError(t('login.error.invalid'));
        }
    };

    return (
        <AuthLayout
            title={t('login.title')}
            subtitle={t('login.subtitle')}
            footer={
                <p className="text-slate-400 text-sm">
                    {t('login.noAccount')}{' '}
                    <button onClick={() => navigate('/register')} className="text-emerald-600 font-bold hover:underline">
                        {t('login.signUp')}
                    </button>
                </p>
            }
        >
            <div className="space-y-6">
                <GoogleButton label={t('google.connectWith')} />
                { /*<StravaButton label={t('strava.connectWith')} /> */ }

                <div className="flex items-center gap-4">
                    <div className="flex-1 h-px bg-slate-200" />
                    <span className="text-sm font-medium text-slate-400">{t('login.or')}</span>
                    <div className="flex-1 h-px bg-slate-200" />
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <AuthInput
                        label={t('login.email.label')}
                        icon={Mail}
                        type="email"
                        placeholder={t('login.email.placeholder')}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                    />

                    <div className="space-y-1">
                        <AuthInput
                            label={t('login.password.label')}
                            icon={Lock}
                            type="password"
                            placeholder={t('login.password.placeholder')}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                        />
                        <div className="flex justify-end pr-1">
                            <button type="button" onClick={() => navigate('/forgot-password')} className="text-xs font-bold text-emerald-600 hover:text-emerald-700">
                                {t('login.forgotPassword')}
                            </button>
                        </div>
                    </div>

                    {error && <ErrorBanner message={error} className="animate-in shake" />}

                    <AuthButton type="submit" isLoading={loading}>
                        {t('login.submit')} <ArrowRight size={20} />
                    </AuthButton>
                </form>
            </div>
        </AuthLayout>
    );
}
